import styled from '@emotion/styled';
import { darken } from 'polished';
import type { FC } from 'react';
import React, { useState, forwardRef } from 'react';
import { animated, useSpring } from 'react-spring';
import { LoadingCircles } from '../../components/loader/LoadingCircles';
import { typography } from '../../styles/typography';
import { shouldForwardProp } from '../../utils';

const getBg = (props: any) => {
  return props['databg']
    ? typeof props['databg'] === 'function'
      ? props['databg'](props)
      : props['databg']
    : props.theme.colors.secondary;
};

const Btn = styled.button<ButtonProps>`
  appearance: none;
  border: none;
  background: none;
  background-color: ${(props) => (props.bordered ? 'none' : getBg(props))};
  border: ${(props) => (props.bordered ? `1px solid ${getBg(props)}` : '')};
  ${typography.h5};
  position: relative;
  font-family: ${(props) => props.theme.font.family};
  color: ${(props) => (props.bordered ? getBg(props) : '#ffffff')};
  min-height: ${(props) => (props.isSmall ? '40px' : '64px')};
  padding: ${(props) => (props.isSmall ? '10px 20px' : '0 38px')};
  position: relative;
  overflow: hidden;
  cursor: pointer;
  width: 100%;
  min-width: ${(props) => (props.isSmall ? '80px' : '220px')};
  ${(props) => props.isSmall && 'max-width: 80px'};

  text-decoration: none;

  display: flex;
  align-items: center;
  justify-content: ${(props) => (!!props.icon ? 'space-between' : 'center')};

  &:focus {
    outline: 1px dashed #666;
  }
`;

const BtnText = styled.div<{ icon: any }>`
  position: relative;
  z-index: 7;
  font-weight: 500;
  flex: 1;
  text-align: ${(props) => (!!props.icon ? 'left' : 'center')};
`;

const BtnBackground = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<any>`
  position: absolute;
  top: 0;
  left: -10px;
  right: -10px;
  bottom: 0;
`;

const BtnBg1 = styled(BtnBackground)<any>`
  background-color: ${(props) => darken(-0.05, getBg(props))};
`;

const BtnBg2 = styled(BtnBackground)<any>`
  background-color: ${(props) => darken(-0.1, getBg(props))};
`;

const Icon = styled.div`
  display: block;
  position: static;
  right: 32px;
  z-index: 12;
  margin-left: 15px;
`;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  title?: string;
  bordered?: boolean;
  bg?: any;
  as?: any;
  icon?: React.ReactNode;
  isSmall?: boolean;
  loading?: boolean;
  [key: string]: any;
}

export const Button: FC<ButtonProps> = forwardRef(function ButtonInner(
  {
    title,
    bg,
    bordered = false,
    onMouseEnter,
    as,
    icon,
    isSmall,
    loading,
    ...rest
  }: ButtonProps,
  ref,
) {
  const [activated, setActivated] = useState(false);
  const [hover, setHover] = useState(false);
  const props = useSpring({
    from: {
      red: '-100%',
      blue: '-110%',
    },
    red: hover ? '0%' : '100%',
    blue: hover ? '0%' : '110%',
    immediate: !activated,
    reset: hover,
    config: {
      mass: 3,
      tension: 440,
      friction: 80,
    },
  });
  if (ref) {
    // noop
  }

  const onEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    setHover(true);
    setActivated(true);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };

  const onLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    setHover(false);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };

  const HostButton = as ? Btn.withComponent(as) : Btn;

  return (
    <HostButton
      {...rest}
      databg={bg}
      bordered={bordered}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      icon={icon}
      isSmall={isSmall}
      title={title}
    >
      <BtnText icon={icon}>
        {loading ? (
          <LoadingCircles color="blue" size="10px" animation="fade" />
        ) : (
          title
        )}
      </BtnText>
      {!!icon && <Icon>{icon}</Icon>}
      <BtnBg1
        databg={bg}
        style={{
          opacity: bordered ? 0.05 : 1,
          transform: props.red.to((x) => `translate3d(${x},0,0)`),
        }}
      />
      <BtnBg2
        databg={bg}
        style={{
          opacity: bordered ? 0.05 : 1,
          transform: props.blue.to((x) => `translate3d(${x},0,0)`),
        }}
      />
    </HostButton>
  );
});
